import { FormikErrors } from 'formik'
import { NewOpportunity } from '../../lib/types'

type FormErrorTree = {
  [key: string]: string | FormErrorTree | FormErrorTree[] | undefined
}

const OPPORTUNITY_FIELD_TEST_IDS: Record<string, string> = {
  opportunity_title: 'opportunity-name',
  asset_class: 'opportunity-asset-class',
  jurisdiction: 'opportunity-jurisdiction',
  currency_code: 'opportunity-currency-code',
  ticket_size: 'opportunity-ticket-size',
  closing_date: 'opportunity-closing-date-container',
  raise_goal: 'opportunity-raise-goal',
  opportunity_summary: 'opportunity-summary',
  short_opportunity_summary: 'opportunity-short-summary',
  cover_image: 'opportunity-cover-image',
  logo_image: 'opportunity-logo-image',
  logo_wide_image: 'opportunity-logo-wide-image',
  marketing_signup_image: 'opportunity-marketing-signup-image-label',
  marketing_signup_mobile_image: 'opportunity-marketing-signup-mobile-image-label',
  status: 'opportunity-visibility-select',
  investment_period_in_days: 'opportunity-investment-period',
  interest_charged_by_credibila_to_opportunity_percent: 'opportunity-interest-charged-credibila',
  expected_spread_percent: 'opportunity-expected-spread',
  days_from_withdraw_request_to_payment: 'opportunity-withdraw-request-days',
  exit_load_percent: 'opportunity-exit-load',
  profit_sharing_to_credibila_percent: 'opportunity-profit-sharing-credibila',
  lock_in_period_in_days: 'opportunity-lock-in-period-days',
  'metadata.asset_fee': 'opportunity-asset-fee',
  'metadata.hurdle_rate': 'opportunity-hurdle-rate',
  'metadata.carry_rate': 'opportunity-carry-rate',
  'metadata.portfolio_service_fee': 'opportunity-portfolio-service-fee',
  'metadata.termsheet_agreement_template_url': 'opportunity-termsheet-agreement-template-url',
  'metadata.underlying_fund': 'opportunity-underlying-fund',
  'metadata.securities_offering': 'opportunity-securities-offering',
  'metadata.reference_valuation': 'opportunity-reference-valuation',
  'metadata.reference_valuation.currency': 'opportunity-reference-valuation-currency',
  'metadata.reference_valuation.amount': 'opportunity-reference-valuation-amount',
}

const resolveFieldTestId = (path: string): string | null => {
  if (OPPORTUNITY_FIELD_TEST_IDS[path]) {
    return OPPORTUNITY_FIELD_TEST_IDS[path]
  }

  const documentMatch = path.match(/^documents\[(\d+)\]\.(title|url)$/)
  if (documentMatch) {
    const index = Number(documentMatch[1]) + 1
    return documentMatch[2] === 'title'
      ? `opportunity-document-${index}-title`
      : `opportunity-document-${index}`
  }

  const metadataField = path.match(/^metadata\.(.+)$/)
  if (metadataField) {
    return `opportunity-${metadataField[1].replace(/_/g, '-')}`
  }

  return null
}

export const findFirstErrorPath = (errors: FormErrorTree, prefix = ''): string | null => {
  for (const [key, value] of Object.entries(errors)) {
    if (value === undefined || value === null) {
      continue
    }

    const path = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'string') {
      return path
    }

    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        const item = value[index]
        const itemPath = `${path}[${index}]`

        if (typeof item === 'string') {
          return itemPath
        }

        if (item && typeof item === 'object') {
          const nestedPath = findFirstErrorPath(item as FormErrorTree, itemPath)
          if (nestedPath) {
            return nestedPath
          }
        }
      }
      continue
    }

    if (typeof value === 'object' && !(value instanceof Date)) {
      const nestedPath = findFirstErrorPath(value as FormErrorTree, path)
      if (nestedPath) {
        return nestedPath
      }
    }
  }

  return null
}

export const scrollToFirstFormError = (errors: FormikErrors<NewOpportunity> | FormErrorTree) => {
  const firstErrorPath = findFirstErrorPath(errors)
  if (!firstErrorPath) {
    return
  }

  const testId = resolveFieldTestId(firstErrorPath)
  const fieldName =
    firstErrorPath
      .split('.')
      .pop()
      ?.replace(/\[\d+\]/, '') ?? firstErrorPath

  const element =
    (testId ? document.querySelector(`[data-testid="${testId}"]`) : null) ||
    document.querySelector(`[name="${fieldName}"]`) ||
    document.getElementById(fieldName)

  if (element instanceof HTMLElement) {
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const focusable = element.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    )

    if (typeof focusable?.focus === 'function') {
      focusable.focus({ preventScroll: true })
    }
  }
}

export const buildTouchedFromErrors = (
  errors: FormikErrors<NewOpportunity> | FormErrorTree,
): Record<string, unknown> => {
  if (!errors || typeof errors !== 'object') {
    return {}
  }

  return Object.entries(errors).reduce<Record<string, unknown>>((touched, [key, value]) => {
    if (value === undefined || value === null) {
      return touched
    }

    if (Array.isArray(value)) {
      touched[key] = value.map((item) =>
        item && typeof item === 'object' ? buildTouchedFromErrors(item as FormErrorTree) : true,
      )
      return touched
    }

    if (typeof value === 'object' && !(value instanceof Date)) {
      touched[key] = buildTouchedFromErrors(value as FormErrorTree)
      return touched
    }

    touched[key] = true
    return touched
  }, {})
}
